import {
  createLocalTrackF,
  toggleEnableNoiseReduction,
  toggleMirrorLocalVideo,
  updateAudioInputDeviceState,
  updateVideoInputDeviceState,
  useBaseStore,
  useConfigStore,
  useShouldMirrorLocalVideo,
  useTracksStore,
  useTranslation,
  type AudioInputDevice,
  type AudioOutputDevice,
  type VideoInputDevice,
} from '@cometchat/calls-sdk-core';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { IconName } from '../icon/icon-paths-24x24';
import Icon from '../icon/Icon.web';
import Video from '../video/Video.web';
import './Settings.web.css';

interface SettingsProps {
  visible?: boolean;
  onClose?: () => void;
  selectedTab?: TabType;
}

type TabType = 'audio' | 'video';
type DropdownType = 'microphone' | 'speaker' | 'camera' | 'framerate';

// Constants
const FRAME_RATES = [5, 15, 30] as const;

const VideoPreview = () => {
  const { t } = useTranslation();
  const previewVideoTrack = useBaseStore(state => state.previewVideoTrack);
  const shouldMirror = useShouldMirrorLocalVideo();
  const previewVideoInputDevice = useBaseStore(
    state => state.previewVideoInputDevice,
  );

  useEffect(() => {
    createLocalTrackF('video', previewVideoInputDevice?.deviceId)
      .then(track => {
        useBaseStore.setState({ previewVideoTrack: track });
      })
      .catch(error => {
        console.error('Error creating local video track:', error);
      });
    return () => {
      useBaseStore.setState({ previewVideoTrack: undefined });
    };
  }, [previewVideoInputDevice]);

  return (
    <div className="cometchat-calls-settings-video-preview__container">
      <Video
        videoTrack={previewVideoTrack}
        className={clsx({
          'cometchat-calls-settings-video-preview__video--mirrored':
            shouldMirror,
        })}
      />
      {!previewVideoTrack && (
        <div className="cometchat-calls-settings-video-preview__placeholder">
          <Icon name="video-fill" size={48} />
          <span>
            {t(
              'controlPanel.settingsDialog.videoSettings.selectCameraToSeePreview',
            )}
          </span>
        </div>
      )}
    </div>
  );
};

const Settings: React.FC<SettingsProps> = ({
  visible = false,
  onClose,
  selectedTab = 'audio',
}) => {
  // State management
  const [activeTab, setActiveTab] = useState<TabType>(selectedTab);
  const audioInputDevices = useBaseStore(state => state.audioInputDevices);
  const audioOutputDevices = useBaseStore(state => state.audioOutputDevices);
  const videoInputDevices = useBaseStore(state => state.videoInputDevices);
  const audioInputDevice = useBaseStore(state => state.audioInputDevice);
  const audioOutputDevice = useBaseStore(state => state.audioOutputDevice);
  const videoInputDevice = useBaseStore(state => state.videoInputDevice);
  const sessionType = useConfigStore(state => state.sessionType);
  const previewVideoInputDevice =
    useBaseStore(state => state.previewVideoInputDevice) || videoInputDevice;
  const [openDropdown, setOpenDropdown] = useState<DropdownType | null>(null);
  const desktopSharingFrameRate = useBaseStore(
    state => state.desktopSharingFrameRate,
  );
  const [frameRate, setFrameRate] = useState(desktopSharingFrameRate);
  const { t } = useTranslation();

  const hasAudioPermission = useTracksStore(state => state.permissions.audio);
  const hasVideoPermission = useTracksStore(state => state.permissions.video);
  const enableNoiseReduction = useBaseStore(
    state => state.enableNoiseReduction,
  );

  const frameRateDropdownRef = useRef<HTMLDivElement | null>(null);
  const mirrorLocalVideo = useBaseStore(state => state.mirrorLocalVideo);

  // References for cleanup
  const permissionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const deviceChangeListenerRef = useRef<(() => void) | null>(null);

  const handleMicDeviceSelect = useCallback((device: MediaDeviceInfo) => {
    updateAudioInputDeviceState(device as AudioInputDevice);
  }, []);

  const handleSpeakerDeviceSelect = useCallback((device: MediaDeviceInfo) => {
    useBaseStore.setState({
      audioOutputDevice: device as AudioOutputDevice,
    });
  }, []);

  const handleCameraDeviceSelect = useCallback(
    async (device: MediaDeviceInfo) => {
      useBaseStore.setState({
        previewVideoInputDevice: device as VideoInputDevice,
      });
    },
    [],
  );

  const onDone = useCallback(() => {
    updateVideoInputDeviceState(previewVideoInputDevice);
    onClose?.();
  }, [onClose, previewVideoInputDevice]);

  // Utility functions
  const getDeviceIcon = (deviceType: string): IconName => {
    switch (deviceType) {
      case 'Microphone':
        return 'mic-fill';
      case 'Speaker':
        return 'speaker-fill';
      case 'Camera':
        return 'video-fill';
      default:
        return 'mic-fill';
    }
  };

  const closeDropdown = useCallback(() => {
    setOpenDropdown(null);
  }, []);

  // Auto-scroll to bottom when frame rate dropdown opens
  useEffect(() => {
    if (openDropdown === 'framerate') {
      const scrollTimeout = setTimeout(() => {
        // Find the dropdown list element
        const dropdownList = document.querySelector(
          '.cometchat-calls-settings-dropdown__list[aria-label="Frame rate options"]',
        );
        const settingsContent = document.querySelector(
          '.cometchat-calls-settings-content',
        );

        if (dropdownList && settingsContent) {
          console.log('Frame rate dropdown opened, checking scroll position');

          const listRect = dropdownList.getBoundingClientRect();
          const contentRect = settingsContent.getBoundingClientRect();

          console.log(
            'List bottom:',
            listRect.bottom,
            'Content bottom:',
            contentRect.bottom,
          );

          // Check if the dropdown list extends below the visible area
          if (listRect.bottom > contentRect.bottom) {
            const scrollAmount = listRect.bottom - contentRect.bottom + 30; // Extra padding
            console.log('Scrolling by:', scrollAmount);

            settingsContent.scrollBy({
              top: scrollAmount,
              behavior: 'smooth',
            });
          }
        } else {
          console.log('Could not find dropdown elements for scrolling');
        }
      }, 10); // Increased delay to ensure dropdown is fully rendered

      return () => clearTimeout(scrollTimeout);
    }
  }, [openDropdown]);

  // Handle clicks outside dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown && event.target instanceof Element) {
        const dropdownElements = document.querySelectorAll(
          '.cometchat-calls-settings-dropdown',
        );
        let clickedInsideDropdown = false;

        dropdownElements.forEach(dropdown => {
          if (dropdown.contains(event.target as Node)) {
            clickedInsideDropdown = true;
          }
        });

        if (!clickedInsideDropdown) {
          closeDropdown();
        }
      }
    };

    if (visible && openDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [visible, openDropdown, closeDropdown]);

  // Real-time permission and device monitoring
  useEffect(() => {
    if (!visible) {
      // Clean up when modal is closed
      if (permissionCheckIntervalRef.current) {
        clearInterval(permissionCheckIntervalRef.current);
        permissionCheckIntervalRef.current = null;
      }
      if (deviceChangeListenerRef.current) {
        navigator.mediaDevices.removeEventListener(
          'devicechange',
          deviceChangeListenerRef.current,
        );
        deviceChangeListenerRef.current = null;
      }
      return;
    }

    // Cleanup on unmount or when visible changes
    return () => {
      if (permissionCheckIntervalRef.current) {
        clearInterval(permissionCheckIntervalRef.current);
        permissionCheckIntervalRef.current = null;
      }
      if (deviceChangeListenerRef.current) {
        navigator.mediaDevices.removeEventListener(
          'devicechange',
          deviceChangeListenerRef.current,
        );
        deviceChangeListenerRef.current = null;
      }
    };
  }, [visible]);

  // Early return if not visible
  if (!visible) {
    return null;
  }

  // Render functions
  const renderTabButton = (tab: TabType, label: string, iconName: IconName) => (
    <button
      key={tab}
      className={`cometchat-calls-settings-tab ${activeTab === tab ? 'cometchat-calls-settings-tab--active' : ''}`}
      onClick={() => setActiveTab(tab)}
      aria-label={`${label} settings`}
      aria-pressed={activeTab === tab}>
      <Icon name={iconName} size={24} fill="#858585" />
      <span className="cometchat-calls-settings-tab__label">{label}</span>
    </button>
  );

  const renderErrorNotification = () => {
    if (hasVideoPermission && hasAudioPermission) {
      return null;
    }

    const devicesWithoutPermission = [];
    if (!hasAudioPermission) {
      devicesWithoutPermission.push('microphone');
    }
    if (!hasVideoPermission) {
      devicesWithoutPermission.push('camera');
    }

    return (
      <div className="cometchat-calls-settings-error-notification">
        <div className="cometchat-calls-settings-error-notification__icon">
          <Icon
            name="error"
            size={20}
            fill="var(--cometchat-calls-permission-error-color)"
          />
        </div>
        <div className="cometchat-calls-settings-error-notification__content">
          <h4 className="cometchat-calls-settings-error-notification__title">
            Some permissions are missing
          </h4>
          <p className="cometchat-calls-settings-error-notification__message">
            Access to your {devicesWithoutPermission.join(' and ')} is blocked.
            Please check your browser settings.
          </p>
        </div>
      </div>
    );
  };

  const renderDeviceDropdown = (
    label: string,
    devices: MediaDeviceInfo[],
    selectedDevice: MediaDeviceInfo | undefined,
    onChange: (device: MediaDeviceInfo) => void,
    dropdownId: DropdownType,
    disabled: boolean = false,
  ) => {
    const isOpen = openDropdown === dropdownId;

    const toggleDropdown = () => {
      if (disabled) {
        return;
      }
      setOpenDropdown(isOpen ? null : dropdownId);
    };

    const selectDevice = (device: MediaDeviceInfo) => {
      if (disabled) {
        return;
      }
      onChange(device);
      closeDropdown();
    };

    return (
      <div className="cometchat-calls-settings-device-section">
        <label className="cometchat-calls-settings-device-label">{label}</label>
        <div className="cometchat-calls-settings-dropdown">
          <button
            className={`cometchat-calls-settings-dropdown__header ${isOpen ? 'cometchat-calls-settings-dropdown__header--open' : ''} ${disabled ? 'cometchat-calls-settings-dropdown__header--disabled' : ''}`}
            onClick={toggleDropdown}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-label={`Select ${label.toLowerCase()}`}
            disabled={disabled}>
            <div className="cometchat-calls-settings-dropdown__selected">
              {selectedDevice && !disabled && (
                <div className="cometchat-calls-settings-dropdown__icon">
                  <Icon name={getDeviceIcon(label)} size={16} fill="#fff" />
                </div>
              )}
              <span className="cometchat-calls-settings-dropdown__text">
                {selectedDevice?.label || `No ${label.toLowerCase()} selected`}
              </span>
            </div>
            {!disabled && (
              <div
                className={`cometchat-calls-settings-dropdown__arrow ${isOpen ? 'cometchat-calls-settings-dropdown__arrow--rotated' : ''}`}>
                <Icon name="arrow-up" size={20} fill="#858585" />
              </div>
            )}
          </button>

          {isOpen && !disabled && (
            <ul
              className="cometchat-calls-settings-dropdown__list"
              role="listbox"
              aria-label={`${label} options`}>
              {devices.map(device => (
                <li
                  key={device.deviceId}
                  className={`cometchat-calls-settings-dropdown__option ${device.deviceId === selectedDevice?.deviceId ? 'cometchat-calls-settings-dropdown__option--selected' : ''}`}
                  onClick={() => selectDevice(device)}
                  role="option"
                  aria-selected={device.deviceId === selectedDevice?.deviceId}>
                  <span className="cometchat-calls-settings-dropdown__option-text">
                    {device.label}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const renderAudioTab = () => (
    <div className="cometchat-calls-settings-tab-content cometchat-calls-settings-tab-content--audio">
      {renderDeviceDropdown(
        t('controlPanel.settingsDialog.deviceLabels.microphone'),
        audioInputDevices,
        audioInputDevice,
        handleMicDeviceSelect,
        'microphone',
      )}
      {renderDeviceDropdown(
        t('controlPanel.settingsDialog.deviceLabels.speaker'),
        audioOutputDevices,
        audioOutputDevice,
        handleSpeakerDeviceSelect,
        'speaker',
      )}
      <div className="cometchat-calls-settings-checkbox">
        <label className="cometchat-calls-settings-checkbox__container">
          <input
            type="checkbox"
            checked={enableNoiseReduction}
            onChange={toggleEnableNoiseReduction}
            className="cometchat-calls-settings-checkbox__input"
          />
          <span
            className="cometchat-calls-settings-checkbox__custom"
            aria-hidden="true"
          />
          <span className="cometchat-calls-settings-checkbox__label">
            {t(
              'controlPanel.settingsDialog.audioSettings.enableNoiseReduction',
            )}
          </span>
        </label>
      </div>
    </div>
  );

  const renderVideoTab = () => (
    <div className="cometchat-calls-settings-tab-content cometchat-calls-settings-tab-content--video">
      {/* Only show video preview if camera permission is granted */}
      {hasVideoPermission && (
        <div className="cometchat-calls-settings-video-preview">
          <VideoPreview />
        </div>
      )}

      <div className="cometchat-calls-settings-video-controls">
        {renderDeviceDropdown(
          t('controlPanel.settingsDialog.deviceLabels.camera'),
          videoInputDevices,
          previewVideoInputDevice,
          handleCameraDeviceSelect,
          'camera',
        )}

        {/* Only show video-related controls if camera permission is granted */}
        {hasVideoPermission && (
          <>
            <div className="cometchat-calls-settings-checkbox">
              <label className="cometchat-calls-settings-checkbox__container">
                <input
                  type="checkbox"
                  checked={mirrorLocalVideo}
                  onChange={toggleMirrorLocalVideo}
                  className="cometchat-calls-settings-checkbox__input"
                  aria-describedby="mirror-video-description"
                />
                <span
                  className="cometchat-calls-settings-checkbox__custom"
                  aria-hidden="true"
                />
                <span className="cometchat-calls-settings-checkbox__label">
                  {t('controlPanel.settingsDialog.videoSettings.mirrorMyVideo')}
                </span>
              </label>
            </div>

            <div className="cometchat-calls-settings-device-section">
              <label className="cometchat-calls-settings-device-label">
                {t('controlPanel.settingsDialog.deviceLabels.frameRate')}
              </label>
              <div
                className="cometchat-calls-settings-dropdown"
                ref={frameRateDropdownRef}>
                <button
                  className={`cometchat-calls-settings-dropdown__header ${openDropdown === 'framerate' ? 'cometchat-calls-settings-dropdown__header--open' : ''}`}
                  onClick={() =>
                    setOpenDropdown(
                      openDropdown === 'framerate' ? null : 'framerate',
                    )
                  }
                  aria-expanded={openDropdown === 'framerate'}
                  aria-haspopup="listbox"
                  aria-label="Select frame rate">
                  <span className="cometchat-calls-settings-dropdown__text">
                    {frameRate}{' '}
                    {t(
                      'controlPanel.settingsDialog.videoSettings.frameRateUnit',
                    )}
                  </span>
                  <div
                    className={`cometchat-calls-settings-dropdown__arrow ${openDropdown === 'framerate' ? 'cometchat-calls-settings-dropdown__arrow--rotated' : ''}`}>
                    <Icon name="arrow-up" size={20} fill="#858585" />
                  </div>
                </button>

                {openDropdown === 'framerate' && (
                  <ul
                    className="cometchat-calls-settings-dropdown__list"
                    role="listbox"
                    aria-label="Frame rate options">
                    {FRAME_RATES.map(rate => (
                      <li
                        key={rate}
                        className={`cometchat-calls-settings-dropdown__option ${rate === frameRate ? 'cometchat-calls-settings-dropdown__option--selected' : ''}`}
                        onClick={() => {
                          setFrameRate(rate);
                          closeDropdown();
                        }}
                        role="option"
                        aria-selected={rate === frameRate}>
                        <span className="cometchat-calls-settings-dropdown__option-text">
                          {rate}{' '}
                          {t(
                            'controlPanel.settingsDialog.videoSettings.frameRateUnit',
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="cometchat-calls-settings-notice">
              <span className="cometchat-calls-settings-notice__text">
                {t('controlPanel.settingsDialog.videoSettings.frameRateNotice')}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'audio':
        return renderAudioTab();
      case 'video':
        return renderVideoTab();
      default:
        return <></>;
    }
  };

  return (
    <div className="cometchat-calls-settings-overlay" onClick={onClose}>
      <div
        className="cometchat-calls-settings-modal"
        onClick={e => e.stopPropagation()}>
        <div className="cometchat-calls-settings-sidebar-wrapper">
          <h2 className="cometchat-calls-settings-sidebar__header">
            {t('controlPanel.settingsDialog.title')}
          </h2>
          <nav
            className="cometchat-calls-settings-sidebar"
            role="navigation"
            aria-label={t('controlPanel.settingsDialog.title')}>
            {renderTabButton(
              'audio',
              t('controlPanel.settingsDialog.tabs.audio'),
              'mic-fill',
            )}
            {sessionType === 'VIDEO' &&
              renderTabButton(
                'video',
                t('controlPanel.settingsDialog.tabs.video'),
                'video-fill',
              )}
          </nav>
        </div>
        <div className="cometchat-calls-settings-content-wrapper">
          <main className="cometchat-calls-settings-body">
            <div className="cometchat-calls-settings-content">
              {renderErrorNotification()}
              {renderContent()}
            </div>
          </main>

          <footer className="cometchat-calls-settings-footer">
            <button
              className="cometchat-calls-settings-button cometchat-calls-settings-button--secondary"
              onClick={onClose}>
              {t('controlPanel.settingsDialog.buttons.cancel')}
            </button>
            <button
              className="cometchat-calls-settings-button cometchat-calls-settings-button--primary"
              onClick={onDone}>
              {t('controlPanel.settingsDialog.buttons.done')}
            </button>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default Settings;
